/**
 * Breadcrumb — meta-component over the shadcn Breadcrumb primitive.
 *
 * The trail is declared entirely through `items`: every item but the last
 * renders as a TanStack Router Link, and the last is the current page
 * (`aria-current="page"`, not a link).
 *
 *   <Breadcrumb
 *     items={[
 *       { label: 'TLC Group', to: '/admin' },
 *       { label: 'Tenant usage', to: '/admin/platform/usage', search: { period: 30 } },
 *       { label: 'Acme Ltd' },
 *     ]}
 *   />
 *
 * Trails longer than three items collapse their middle items into an
 * ellipsis menu. `collapse="responsive"` (the default) collapses only below
 * the `md` breakpoint; `"always"` and `"never"` pin it either way.
 */

import { Link } from '@tanstack/react-router';
import {
  Breadcrumb as ShadBreadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { FOCUS_RING } from '../_styles';

export interface BreadcrumbItemSpec {
  label: string;
  /** Route path. Omitted on the current page, which is never a link. */
  to?: string;
  /** Path params for a `to` with `$segments`. */
  params?: Record<string, string>;
  /** Search params carried by the link (e.g. the period a list was showing). */
  search?: Record<string, unknown>;
}

export type BreadcrumbCollapse = 'responsive' | 'always' | 'never';

export interface BreadcrumbProps {
  items: BreadcrumbItemSpec[];
  /** When a trail of more than three items hides its middle. Defaults to `responsive`. */
  collapse?: BreadcrumbCollapse;
  className?: string;
}

/** Trails up to this length never collapse: first, parent and current all fit. */
const MAX_UNCOLLAPSED = 3;

/** Long labels are cut with an ellipsis; the full text stays in `title`. */
const LABEL_TRUNCATE = 'block max-w-[10rem] truncate sm:max-w-[16rem]';

/** Earlier crumbs are ancestors of the page, so never "active" (see CrumbLink). */
const EXACT_ONLY = { exact: true } as const;

function CrumbLink({ item, className }: { item: BreadcrumbItemSpec & { to: string }; className?: string }) {
  return (
    <BreadcrumbLink asChild>
      <Link
        to={item.to}
        params={item.params}
        search={item.search}
        // An earlier crumb's path is a prefix of the address, which the router
        // would call active and mark aria-current="page"; only the last crumb
        // is the current page.
        activeOptions={EXACT_ONLY}
        title={item.label}
        className={cn(LABEL_TRUNCATE, 'rounded-sm', FOCUS_RING, className)}
      >
        {item.label}
      </Link>
    </BreadcrumbLink>
  );
}

function Crumb({ item, current }: { item: BreadcrumbItemSpec; current: boolean }) {
  if (current || !item.to) {
    // The primitive announces the current page as a disabled link; it is
    // plain text here, so drop that role and keep aria-current.
    return (
      <BreadcrumbPage
        role={undefined}
        aria-disabled={undefined}
        aria-current={current ? 'page' : undefined}
        title={item.label}
        className={cn(LABEL_TRUNCATE, current && 'font-medium')}
      >
        {item.label}
      </BreadcrumbPage>
    );
  }
  return <CrumbLink item={{ ...item, to: item.to }} />;
}

function HiddenCrumbsMenu({ items }: { items: BreadcrumbItemSpec[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Show hidden breadcrumbs"
        className={cn('rounded-sm hover:text-foreground', FOCUS_RING)}
      >
        <BreadcrumbEllipsis className="size-5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-auto min-w-40">
        {items.map((item, idx) => (
          <DropdownMenuItem key={`${item.label}-${idx}`} asChild>
            {item.to ? (
              <Link to={item.to} params={item.params} search={item.search} activeOptions={EXACT_ONLY}>
                {item.label}
              </Link>
            ) : (
              <span>{item.label}</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Breadcrumb({ items, collapse = 'responsive', className }: BreadcrumbProps) {
  if (items.length === 0) return null;

  const collapsible = collapse !== 'never' && items.length > MAX_UNCOLLAPSED;
  // Keep the root and the last two (parent + current); the rest can fold away.
  const hidden = collapsible ? items.slice(1, -2) : [];
  const hiddenFrom = 1;
  const hiddenTo = hiddenFrom + hidden.length;
  const lastIdx = items.length - 1;

  // Responsive: the ellipsis shows below md, the hidden items from md up.
  const ellipsisClass = collapse === 'responsive' ? 'md:hidden' : undefined;
  const hiddenItemClass = collapse === 'responsive' ? 'hidden md:inline-flex' : undefined;

  return (
    <ShadBreadcrumb aria-label="Breadcrumb" className={className}>
      <BreadcrumbList>
        {items.map((item, idx) => {
          const isHidden = idx >= hiddenFrom && idx < hiddenTo;
          if (isHidden && collapse === 'always') {
            if (idx !== hiddenFrom) return null;
            return [
              <BreadcrumbSeparator key="sep-ellipsis" />,
              <BreadcrumbItem key="ellipsis">
                <HiddenCrumbsMenu items={hidden} />
              </BreadcrumbItem>,
            ];
          }
          return [
            idx === hiddenFrom && hidden.length > 0 && collapse === 'responsive' && [
              <BreadcrumbSeparator key="sep-ellipsis" className={ellipsisClass} />,
              <BreadcrumbItem key="ellipsis" className={ellipsisClass}>
                <HiddenCrumbsMenu items={hidden} />
              </BreadcrumbItem>,
            ],
            idx > 0 && (
              <BreadcrumbSeparator
                key={`sep-${idx}`}
                className={isHidden ? hiddenItemClass : undefined}
              />
            ),
            <BreadcrumbItem
              key={`item-${idx}`}
              className={cn('min-w-0', isHidden && hiddenItemClass)}
            >
              <Crumb item={item} current={idx === lastIdx} />
            </BreadcrumbItem>,
          ];
        })}
      </BreadcrumbList>
    </ShadBreadcrumb>
  );
}
